// src/components/features/timetables/Controls/TimetableControls.tsx
import type { ComponentProps } from 'preact';
import { Button } from '../../../base/Button/Button';
import { SvgIcon } from '../../../base/Icon/SvgIcon';
// styles
import styles from './TimetableControls.module.css';

const defaultDirectionLabels: Record<'outbound' | 'inbound', string> = {
  outbound: 'Outbound',
  inbound: 'Inbound',
};

export interface TimetableControlsProps extends Omit<
  ComponentProps<'form'>,
  'children' | 'onSubmit'
> {
  direction: 'outbound' | 'inbound';
  directionLabels?: Record<'outbound' | 'inbound', string>;
  stopDisplay: 'major' | 'all';
  date: string;
  loading?: boolean;
  onDirectionChange: (value: 'outbound' | 'inbound') => void;
  onStopDisplayChange: (value: 'major' | 'all') => void;
  onDateChange: (value: string) => void;
  onSubmit: (event: SubmitEvent) => void;
}

function joinClassNames(...classNames: Array<string | false | undefined>) {
  return classNames.filter(Boolean).join(' ');
}

/**
 * Holds interactive controls used to refine timetable output.
 *
 * Intended for direction, date, and stop filter inputs.
 */
export function TimetableControls({
  direction,
  directionLabels = defaultDirectionLabels,
  stopDisplay,
  date,
  loading = false,
  onDirectionChange,
  onStopDisplayChange,
  onDateChange,
  onSubmit,
  className,
  ...props
}: TimetableControlsProps) {
  return (
    <form
      {...props}
      className={className ? `${styles.form} ${className}` : styles.form}
      onSubmit={(event) => onSubmit(event as SubmitEvent)}
    >
      <fieldset className={styles.section}>
        <legend className={styles.srOnly}>Direction</legend>
        <div
          className={joinClassNames(styles.optionGrid, styles.directionGrid)}
        >
          <label className={styles.optionLabel}>
            <input
              type="radio"
              name="direction"
              value="outbound"
              className={styles.hiddenInput}
              checked={direction === 'outbound'}
              onChange={() => onDirectionChange('outbound')}
            />
            <span
              className={joinClassNames(
                styles.optionCard,
                direction === 'outbound' && styles.optionSelected,
              )}
            >
              <span className={styles.optionContent}>
                <SvgIcon className={styles.optionIcon} name="destination" />
                <span>{directionLabels.outbound}</span>
              </span>
            </span>
          </label>

          <label className={styles.optionLabel}>
            <input
              type="radio"
              name="direction"
              value="inbound"
              className={styles.hiddenInput}
              checked={direction === 'inbound'}
              onChange={() => onDirectionChange('inbound')}
            />
            <span
              className={joinClassNames(
                styles.optionCard,
                direction === 'inbound' && styles.optionSelected,
              )}
            >
              <span className={styles.optionContent}>
                <SvgIcon className={styles.optionIcon} name="destination" />
                <span>{directionLabels.inbound}</span>
              </span>
            </span>
          </label>
        </div>
      </fieldset>

      <fieldset className={styles.section}>
        <legend className={styles.srOnly}>Stops</legend>
        <div className={styles.optionGrid}>
          <label className={styles.optionLabel}>
            <input
              type="radio"
              name="stop-display"
              value="major"
              className={styles.hiddenInput}
              checked={stopDisplay === 'major'}
              onChange={() => onStopDisplayChange('major')}
            />
            <span
              className={joinClassNames(
                styles.optionCard,
                stopDisplay === 'major' && styles.optionSelected,
              )}
            >
              <span className={styles.optionContent}>
                <SvgIcon className={styles.optionIcon} name="majorStops" />
                <span>Major stops</span>
              </span>
            </span>
          </label>

          <label className={styles.optionLabel}>
            <input
              type="radio"
              name="stop-display"
              value="all"
              className={styles.hiddenInput}
              checked={stopDisplay === 'all'}
              onChange={() => onStopDisplayChange('all')}
            />
            <span
              className={joinClassNames(
                styles.optionCard,
                stopDisplay === 'all' && styles.optionSelected,
              )}
            >
              <span className={styles.optionContent}>
                <SvgIcon className={styles.optionIcon} name="allStops" />
                <span>All stops</span>
              </span>
            </span>
          </label>
        </div>
      </fieldset>

      <div className={styles.footerRow}>
        <label className={styles.dateField}>
          <span className={styles.srOnly}>Date</span>
          <input
            className={styles.dateInput}
            type="date"
            aria-label="Date"
            value={date}
            onInput={(event) => onDateChange(event.currentTarget.value)}
          />
        </label>

        <Button type="submit" loading={loading}>
          Update
        </Button>
      </div>
    </form>
  );
}
